
import filters from "@/filters";
import { Component, Vue, Watch } from "vue-property-decorator";
import CourseItem from "@/components/CourseItem.vue";
import CourseAccordion from "@/components/CourseAccordion.vue";
import MyCourseAssessment from "@/views/my-course/MyCourseAssessment.vue";
import MyCourseContent from "@/views/my-course/MyCourseContent.vue";
import MyCourseAttachment from "@/views/my-course/MyCourseAttachment.vue";
import { CourseSectionEnum } from "@/constants/enum.constant";
import { random, downloadBlob, download } from "@/functions/global.function";
import MyCourseRating from "@/views/my-course/MyCourseRating.vue";
import MyCourseCertificate from "@/views/my-course/MyCourseCertificate.vue";
import { Action, State } from "vuex-class";
import { AxiosResponse } from "axios";
import _ from "lodash";
import MyCourseEvaluation from "@/views/my-course/MyCourseEvaluation.vue";

@Component({
  components: {
    MyCourseEvaluation,
    MyCourseCertificate,
    MyCourseRating,
    MyCourseAttachment,
    MyCourseContent,
    MyCourseAssessment,
    CourseAccordion,
    CourseItem,
  },
  filters,
})
export default class MyCourseDetail extends Vue {
  @Action("getMyCourseDetail", { namespace: "myCourse" })
  private getMyCourseDetail: any;
  @Action("getCertificate", { namespace: "myCourse" })
  private getCertificate: any;
  
  @State('user', { namespace: 'auth' })
	private user!: any;

  private course: any = null;
  private section = CourseSectionEnum.PRE_TEST;
  private step = 1;
  private attachments = Array.from({ length: 5 }).map(
    (value, index) => index + 1
  );
  private contentsHash = random();
  private attachmentsHash = random();
  private ratingHash = random();
  private windowWidth = window.innerWidth;
  private isLoading = false;

  @Watch("step")
  private onStep() {
    switch (this.section) {
      case CourseSectionEnum.PRE_TEST:
        if (this.step > this.preTestTotalQuestion + 1) this.next();
        break;
      case CourseSectionEnum.CONTENTS:
        console.log(this.step);
        if (this.step > this.course?.contents.length) this.next();
        break;
      case CourseSectionEnum.POST_TEST:
        if (this.step > this.postTestTotalQuestion + 1) this.next();
        break;
      case CourseSectionEnum.ATTACHMENTS:
        if (this.step > this.course?.attachments.length) this.next();
        break;
      case CourseSectionEnum.RATING:
        if (this.step > 1) this.next();
        break;
      case CourseSectionEnum.EVALUATION:
        if (this.step > this.preTestTotalQuestion + this.postTestTotalQuestion)
          this.next();
        break;
      default:
        break;
    }
  }

  private get isMobile() {
    return this.windowWidth < 768;
  }

  private get isPreTestSection() {
    return this.section === CourseSectionEnum.PRE_TEST;
  }
  private get isContentsSection() {
    return this.section === CourseSectionEnum.CONTENTS;
  }
  private get isPostTestSection() {
    return this.section === CourseSectionEnum.POST_TEST;
  }
  private get isAttachmentsSection() {
    return this.section === CourseSectionEnum.ATTACHMENTS;
  }
  private get isRatingSection() {
    return this.section === CourseSectionEnum.RATING;
  }
  private get isEvaluationSection() {
    return this.section === CourseSectionEnum.EVALUATION;
  }
  private get isCertificateSection() {
    return this.section === CourseSectionEnum.CERTIFICATE;
  }
  private get preTestTotalQuestion() {
    return _.sum(
      this.course?.pre_test.sections.map((section) => section.questions.length)
    );
  }
  private get postTestTotalQuestion() {
    return _.sum(
      this.course?.post_test.sections.map((section) => section.questions.length)
    );
  }

  private created() {
    this.getData();
  }

  private mounted() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  private beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }

  private handleResize() {
    this.windowWidth = window.innerWidth;
  }

  private getData() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.getMyCourseDetail({
      params: {
        slug: this.$route.params.slug,
      },
      success: (response: AxiosResponse) => {
        this.course = response.data.data;

        if (!this.course?.paid) {
          this.$router.push({
            name: "home",
          });
        }
      },
      finally: () => {
        this.isLoading = false;
      },
    });
  }

  private jump(section, step) {
    switch (section) {
      case "pre-test":
        this.section = CourseSectionEnum.PRE_TEST;
        break;
      case "contents":
        if (this.isLockContent(step)) return false;
        this.section = CourseSectionEnum.CONTENTS;
        break;
      case "post-test":
        if (this.isLockPostTest) return false;
        this.section = CourseSectionEnum.POST_TEST;
        break;
      case "attachments":
        this.section = CourseSectionEnum.ATTACHMENTS;
        break;
      case "rating":
        this.section = CourseSectionEnum.RATING;
        break;
      case "evaluation":
        if (this.isLockEvaluation) return false;
        this.section = CourseSectionEnum.EVALUATION;
        break;
      case "certificate":
        if (this.isLockCertificate) return false;
        this.section = CourseSectionEnum.CERTIFICATE;
        break;
      default:
        break;
    }
    this.step = step;
  }

  private nextStep(step) {
    switch (this.section) {
      case CourseSectionEnum.CONTENTS:
        if (this.isLockContent(step)) return false;
        break;
      case CourseSectionEnum.POST_TEST:
        if (this.isLockPostTest) return false;
        break;
      case CourseSectionEnum.CERTIFICATE:
        if (this.isLockCertificate) return false;
        break;
      default:
        break;
    }
    this.step = step;
  }

  private isLockContent(step = 1) {
    if (step === 1) {
      return this.course?.pre_test.result === null;
    } else {
      const track =
        parseInt(this.course?.contents[step - 2]?.track?.total_played) || 0;
      return track <= 0;
    }
  }

  private get isLockPostTest() {
    const content = this.course?.contents[this.course?.contents.length - 1];
    if (content?.track) {
      return (
        content?.track.total_played <= 0 &&
        (content?.track.last_play / content.duration) * 100 < 75
      );
    } else {
      return true;
    }
  }

  private get isLockEvaluation() {
    return this.isLockCertificate;
  }

  private get isLockCertificate() {
    return this.course?.post_test.result < 75;
  }

  private isContentDone(index) {
    const track =
      parseInt(this.course?.contents[index]?.track?.total_played) || 0;
    return track > 0;
  }

  private downloadCertificateMobile() {
    // Only allow download on mobile devices
    if (this.windowWidth < 768 && !this.isLockCertificate) {
      window.open(this.course?.certificate?.attachment, '_blank');
    }
  }

  private downloadAttachmentMobile(attachment: any) {
    // Only allow download on mobile devices
    if (this.windowWidth < 768) {
      // Download the attachment file using the download function
      // which handles URL to Blob conversion automatically
      window.open(attachment.attachment, '_blank');
    }
  }

  private next() {
    switch (this.section) {
      case CourseSectionEnum.PRE_TEST:
        this.section = CourseSectionEnum.CONTENTS;
        if (
          document.getElementById(`panel-${this.course?.contentsHash}`)?.style
            .height !== "auto"
        ) {
          document.getElementById(this.course?.contentsHash)?.click();
        }
        break;
      case CourseSectionEnum.CONTENTS:
        this.section = CourseSectionEnum.POST_TEST;
        break;
      case CourseSectionEnum.POST_TEST:
        this.section = CourseSectionEnum.ATTACHMENTS;
        if (
          document.getElementById(`panel-${this.attachmentsHash}`)?.style
            .height !== "auto"
        ) {
          document.getElementById(this.attachmentsHash)?.click();
        }
        break;
      case CourseSectionEnum.ATTACHMENTS:
        this.section = CourseSectionEnum.RATING;
        if (
          document.getElementById(`panel-${this.ratingHash}`)?.style.height !==
          "auto"
        ) {
          document.getElementById(this.ratingHash)?.click();
        }
        break;
      case CourseSectionEnum.RATING:
        this.section = CourseSectionEnum.EVALUATION;
        break;
      case CourseSectionEnum.EVALUATION:
        this.section = CourseSectionEnum.CERTIFICATE;
        break;
      default:
        break;
    }
    this.step = 1;
  }
}
